import { getRecordFormat, RECORD_FORMATS, type RecordFormatKey } from '@adhd/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '../components/Icons.js';
import { BlankRecord } from '../components/BlankRecord.js';

/**
 * Blank worksheets to print and fill in by hand.
 *
 * Its own screen because it is its own job. This used to be a checkbox on the
 * export screen, and switching it on quietly repurposed everything around it:
 * the date range stopped meaning anything, the heading changed, and the print
 * button changed under your finger. Two screens wearing one coat, where the
 * only way to tell which one you were looking at was to notice what had
 * vanished.
 *
 * Nothing here reads the database. There is no range to choose, no records to
 * fetch, no loading state and no error state — which is the plainest evidence
 * that it never belonged beside the export controls.
 *
 * A blank is the same component the export screen prints your records with,
 * handed nothing. BlankRecord walks the format's own steps, so an
 * empty array draws the real grid with empty cells instead of a second layout
 * that would have to be kept in step with the first one forever. It also means
 * a blank sheet holds three situations exactly as a filled one does; the
 * vertical form this replaces spilled a single situation over two pages.
 *
 * The grid prints sideways, which is a property of the sheet of paper rather
 * than of any element on it, so it is switched with a class on <body> that the
 * print stylesheet reads.
 */

/**
 * How much paper a person plausibly wants at once — one to try, a couple for a
 * bag, a small stack for a drawer. Nothing larger, because a number you cannot
 * picture as a pile of paper is a number chosen by accident.
 */
const PAGE_COUNTS = [1, 2, 4, 8];

export function Worksheets(): JSX.Element {
  const navigate = useNavigate();
  const [formatKey, setFormatKey] = useState<RecordFormatKey>('standard');
  const [pages, setPages] = useState(2);

  // Not RECORD_FORMATS.find, which is RecordFormat | undefined and would want a
  // `!` this cannot prove. The shared helper falls back to the standard format,
  // so there is no state of this screen that renders nothing.
  const format = getRecordFormat(formatKey);

  // No landscape class here any more. This screen used to force one, because
  // the blank was a 900px-wide grid; the blank is now the portrait record with
  // room to write, so the default page orientation is the right one. Setting it
  // would rotate the page under a layout built for the tall axis.

  return (
    <div className="stack-lg stack">
      {/* Marked no-print, unlike the export screen's header, and the difference
          is deliberate: there the h1 is the document's own title because the
          document needs a name and a date on it. A blank carries neither — it
          will be filled in some other evening, and dating it today would date
          it wrongly before anyone has written a word — and each sheet already
          prints its own name and its own "when to reach for this" across the
          top, so an app heading above the first one is a second title. */}
      <header className="page-header no-print">
        <button
          className="btn btn-ghost btn-compact"
          style={{ marginBottom: 'var(--space-3)', paddingLeft: 0 }}
          onClick={() => navigate('/')}
        >
          <ArrowLeftIcon /> Back to today
        </button>
        <h1>Blank worksheets</h1>
        <p>
          Empty worksheets to fill in by hand. Some of the moments these are for are the ones where
          picking up your phone is not the thing you can do — a few in a bag, or by the bed, cover
          that.
        </p>
      </header>

      <div className="no-print stack">
        <p className="faint">
          Nothing written on paper finds its way back into the app. You can type it in afterwards if
          you want it in the log.
        </p>

        <section className="stack-sm stack">
          <h2 className="section-head">Which one</h2>
          {/* The same five formats the app offers when you start a record, with
              the same names and the same descriptions of when to reach for
              them. A printed worksheet that quietly differed from the one on
              screen would be a second, slightly wrong version of the method to
              learn. */}
          <div className="stack-sm stack" role="radiogroup" aria-label="Which worksheet">
            {RECORD_FORMATS.map((f) => (
              <label className="option option-row" key={f.key}>
                <input
                  type="radio"
                  name="worksheet-format"
                  checked={formatKey === f.key}
                  onChange={() => setFormatKey(f.key)}
                  style={{ accentColor: 'var(--accent)' }}
                />
                {/* A div rather than a span: .option span is the muted
                    description style, and nesting the title inside one greys
                    the title too. */}
                <div className="grow" style={{ display: 'grid' }}>
                  <strong>{f.name}</strong>
                  <span>{f.when}</span>
                </div>
              </label>
            ))}
          </div>
        </section>

        <section className="stack-sm stack">
          <h2 className="section-head">How many</h2>
          {/* One worksheet per page now, where the old landscape grid held
              three situations on each. Said plainly, because "8 pages" meaning
              twenty-four situations was the kind of surprise you only discover
              at the printer. */}
          <p className="muted">
            One worksheet per page, with room to write in each section by hand.
          </p>
          <div className="chips" role="group" aria-label="How many pages">
            {PAGE_COUNTS.map((n) => (
              <button
                key={n}
                className="chip"
                aria-pressed={pages === n}
                onClick={() => setPages(n)}
              >
                {n} {n === 1 ? 'page' : 'pages'}
              </button>
            ))}
          </div>
          <p className="faint">
            {pages} {pages === 1 ? 'worksheet' : 'worksheets'}, one per sheet of paper.
          </p>
        </section>
      </div>

      {/* The sheets, on screen, as they will be on paper — the same rule the
          export screen runs on. What is below this line is what comes out of
          the printer, so changing the format changes the thing you are looking
          at rather than a preview of it.

          No sheet numbers: these are identical blanks that will be filled in
          weeks apart, in whatever order they come off the pile, so numbering
          them would assert an order that does not exist. And no sideways
          scroll box any more — the blank is portrait, the same shape as the
          record it becomes, so it fits the page it is read on. */}
      {Array.from({ length: pages }, (_, i) => (
        <div className="doc-stage" key={i}>
          <BlankRecord format={format} />
        </div>
      ))}

      {/* Last child on purpose, and the placement is the whole trick.
          `position: sticky` with `bottom` pins an element only while there is
          still content BELOW it to scroll through, so anywhere above the sheets
          its natural position simply travels up and off the screen at the first
          scroll. Before that it sat in the normal flow near the bottom of the
          viewport, underneath the fixed bottom nav, which is painted over it and
          wins the hit test — tapping where the button appeared navigated away
          instead of printing. At the end of the container it stays pinned above
          the nav for the whole length of the stack and releases at the bottom. */}
      <div className="print-action no-print">
        <button className="btn btn-primary btn-block" onClick={() => window.print()}>
          {/* Names what is about to come out, because the choices that decided
              it may be scrolled far above this button by the time you reach it. */}
          Print {pages} blank {pages === 1 ? 'page' : 'pages'}
        </button>
        {/* Said once, and quietly. There is no separate download here, and
            people who do not know the print dialogue can save a file go looking
            for a button that does not exist. */}
        <p className="faint" style={{ textAlign: 'center', marginTop: 'var(--space-2)' }}>
          For a file rather than paper, choose Save as PDF as the destination in the print dialogue.
        </p>
      </div>
    </div>
  );
}
